import { useEffect, useMemo, type PropsWithChildren } from 'react';
import { Buffer } from 'buffer';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { LedgerWalletAdapter } from '@solana/wallet-adapter-ledger';
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom';
import { SolflareWalletAdapter } from '@solana/wallet-adapter-solflare';
import '@solana/wallet-adapter-react-ui/styles.css';
import { SolanaContext } from './solanaContext.ts';

const appUrl =
  import.meta.env.VITE_APP_URL ??
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173');
const appIcon = new URL('/favicon.svg', appUrl).toString();
const solanaEndpoint = import.meta.env.VITE_SOLANA_RPC_URL ?? 'https://api.mainnet-beta.solana.com';
const browserGlobal = globalThis as typeof globalThis & { Buffer?: typeof Buffer };

if (browserGlobal.Buffer === undefined) {
  browserGlobal.Buffer = Buffer;
}

export function SolanaWalletProvider({ children }: PropsWithChildren) {
  useEffect(() => {
    void import('@metamask/connect-solana')
      .then(({ createSolanaClient }) => createSolanaClient({
        dapp: {
          name: 'Emergency Exit',
          url: appUrl,
          iconUrl: appIcon,
        },
        api: {
          supportedNetworks: {
            mainnet: solanaEndpoint,
          },
        },
      }))
      .catch((error) => {
        console.warn('Failed to register MetaMask Solana wallet', error);
      });
  }, []);

  const wallets = useMemo(
    () => [
      new SolflareWalletAdapter({ network: WalletAdapterNetwork.Mainnet }),
      new PhantomWalletAdapter(),
      new LedgerWalletAdapter(),
    ],
    [],
  );

  const contextValue = useMemo(
    () => ({
      chainId: 900900900,
    }),
    [],
  );

  return (
    <SolanaContext.Provider value={contextValue}>
      <ConnectionProvider endpoint={solanaEndpoint}>
        <WalletProvider wallets={wallets} autoConnect>
          <WalletModalProvider>{children}</WalletModalProvider>
        </WalletProvider>
      </ConnectionProvider>
    </SolanaContext.Provider>
  );
}
